import { reactive, computed } from 'vue'
import http from '../helpers/http.js'

const STUN_CONFIG = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] }

export function useWebRTC(token, myPeerId) {
  const state = reactive({
    localStream: null,
    streams: {},              // peerId → MediaStream
    peerConnections: {},      // peerId → RTCPeerConnection
  })

  // ─── Computed properties ──────────────────────────────────────────────
  const remotePeerIds = computed(() =>
    Object.keys(state.streams).filter(id => id !== myPeerId)
  )

  // ─── Peer connection creation ──────────────────────────────────────────
  function makePeerConnection(peerId) {
    if (state.peerConnections[peerId]) {
      return state.peerConnections[peerId]
    }

    const pc = new RTCPeerConnection(STUN_CONFIG)
    state.peerConnections[peerId] = pc

    pc.onicecandidate = ({ candidate }) => {
      if (candidate) {
        console.log({ candidate })
        sendSignal(peerId, { type: 'ice', candidate: candidate.toJSON() })
      }
    }

    pc.ontrack = ({ streams: [remoteStream] }) => {
      state.streams[peerId] = remoteStream
    }

    // Add local tracks if we already have a stream
    if (state.localStream) {
      state.localStream.getTracks().forEach(t => pc.addTrack(t, state.localStream))
    }

    return pc
  }

  // ─── Signaling ────────────────────────────────────────────────────────
  async function sendSignal(to_id, payload) {
    try {
      await http.post('/api/hammershark/signal', {
        token,
        from_id: myPeerId,
        to_id,
        payload,
      })
    } catch (err) {
      console.error('sendSignal failed:', err)
    }
  }

  // ─── Offer/Answer exchange ────────────────────────────────────────────
  async function offerTo(peerId) {
    const pc = makePeerConnection(peerId)
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    sendSignal(peerId, { type: 'offer', sdp: pc.localDescription })
  }

  async function handleOffer(fromId, sdp) {
    const pc = makePeerConnection(fromId)
    await pc.setRemoteDescription(new RTCSessionDescription(sdp))
    const answer = await pc.createAnswer()
    await pc.setLocalDescription(answer)
    sendSignal(fromId, { type: 'answer', sdp: pc.localDescription })
  }

  async function handleAnswer(fromId, sdp) {
    const pc = state.peerConnections[fromId]
    if (!pc) return
    await pc.setRemoteDescription(new RTCSessionDescription(sdp))
  }

  // ─── ICE candidate handling ───────────────────────────────────────────
  async function handleIce(fromId, candidate) {
    const pc = state.peerConnections[fromId]
    if (!pc) return
    try {
      await pc.addIceCandidate(new RTCIceCandidate(candidate))
    } catch (err) {
      // ICE candidate already added or invalid
    }
  }

  // ─── Polling ──────────────────────────────────────────────────────────
  async function pollSignals() {
    try {
      const res = await http.get(
        `/api/hammershark/signal?token=${token}&peer_id=${myPeerId}`
      )
      const { signals, peers } = res.data

      // Discover new peers; offer to them if we have a local stream
      for (const peerId of peers) {
        if (peerId === myPeerId) continue
        if (!state.peerConnections[peerId]) {
          makePeerConnection(peerId)
          if (state.localStream) {
            await offerTo(peerId)
          }
        }
      }

      // Process incoming signals
      for (const { from_id, payload } of signals) {
        if (payload.type === 'offer') {
          await handleOffer(from_id, payload.sdp)
        } else if (payload.type === 'answer') {
          await handleAnswer(from_id, payload.sdp)
        } else if (payload.type === 'ice') {
          await handleIce(from_id, payload.candidate)
        }
      }
    } catch (err) {
      console.error('pollSignals failed:', err)
    }
  }

  // ─── Camera enumeration ──────────────────────────────────────────────
  async function getAvailableCameras() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      return devices.filter(device => device.kind === 'videoinput')
    } catch (err) {
      console.error('enumerateDevices failed', err)
      return []
    }
  }

  // ─── Camera control ───────────────────────────────────────────────────
  async function toggleCamera(deviceId = null) {
    if (state.localStream) {
      state.localStream.getTracks().forEach(t => t.stop())
      state.localStream = null
      delete state.streams[myPeerId]
      return
    }

    try {
      const constraints = deviceId ? { video: { deviceId: { exact: deviceId } } } : { video: true }
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      state.localStream = stream
      state.streams[myPeerId] = stream

      // Add track to any existing peer connections and re-offer
      for (const peerId of Object.keys(state.peerConnections)) {
        const pc = state.peerConnections[peerId]
        stream.getTracks().forEach(t => pc.addTrack(t, stream))
        await offerTo(peerId)
      }
    } catch (err) {
      console.error('getUserMedia failed', err)
      alert('Could not access camera: ' + err.message)
    }
  }

  // ─── Lifecycle ────────────────────────────────────────────────────────
  let pollInterval = null

  function startPolling() {
    pollSignals()
    pollInterval = setInterval(pollSignals, 5000)
  }

  function cleanup() {
    if (pollInterval) {
      clearInterval(pollInterval)
    }
    if (state.localStream) {
      state.localStream.getTracks().forEach(t => t.stop())
    }
    Object.values(state.peerConnections).forEach(pc => pc.close())
  }

  return {
    // Reactive state
    localStream: computed(() => state.localStream),
    streams: computed(() => state.streams),
    remotePeerIds,

    // Methods
    toggleCamera,
    getAvailableCameras,
    startPolling,
    cleanup,
  }
}

